import { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import {
  ChevronLeft,
  CheckCircle,
  AlertTriangle,
  Edit3,
  RotateCcw,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { track } from '@/lib/analytics';
import { useProfileStore } from '@/lib/state/profile-store';
import {
  ExtractedStartupData,
  getConfidenceColor,
  buildReviewedDeckProfile,
} from '@/lib/pitch-deck-extraction';
import AnimatedPressable from '@/components/AnimatedPressable';
import { reportError } from '@/lib/errors';

interface EditableField {
  key: string;
  label: string;
  value: string;
  confidence: number;
  multiline?: boolean;
}

export default function PitchDeckReviewScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const params = useLocalSearchParams<{ extractedData: string }>();

  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const setProfileMethod = useProfileStore((s) => s.setProfileMethod);

  const [extractedData, setExtractedData] = useState<ExtractedStartupData | null>(null);
  // Distinguishes "results couldn't be read" from "still initializing" so we never
  // strand the founder on a permanent "Loading…" when the param is missing/corrupt.
  const [parseError, setParseError] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!params.extractedData) {
      setParseError(true);
      return;
    }
    {
      try {
        const data = JSON.parse(params.extractedData) as ExtractedStartupData;
        setExtractedData(data);
        setParseError(false);

        // Initialize field values
        setFieldValues({
          name: String(data.name.value),
          tagline: String(data.tagline.value),
          industry: String(data.industry.value),
          stage: String(data.stage.value),
          problem: String(data.problem.value),
          solution: String(data.solution.value),
          targetCustomer: String(data.targetCustomer.value),
          marketSize: String(data.marketSize.value),
          fundingTarget: String(data.fundingTarget.value),
          teamSize: String(data.teamSize.value),
          competitors: Array.isArray(data.competitors.value)
            ? data.competitors.value.join(', ')
            : String(data.competitors.value),
          location: String(data.location.value),
          foundedYear: String(data.foundedYear.value),
        });
      } catch (e) {
        reportError(e, { context: 'PitchDeckReview.parseExtractedData', level: 'warning' });
        setParseError(true);
      }
    }
  }, [params.extractedData]);

  const fields: EditableField[] = extractedData
    ? [
        { key: 'name', label: 'Company Name', value: fieldValues.name || '', confidence: extractedData.name.confidence },
        { key: 'tagline', label: 'Tagline', value: fieldValues.tagline || '', confidence: extractedData.tagline.confidence },
        { key: 'industry', label: 'Industry', value: fieldValues.industry || '', confidence: extractedData.industry.confidence },
        { key: 'stage', label: 'Stage', value: fieldValues.stage || '', confidence: extractedData.stage.confidence },
        { key: 'problem', label: 'Problem', value: fieldValues.problem || '', confidence: extractedData.problem.confidence, multiline: true },
        { key: 'solution', label: 'Solution', value: fieldValues.solution || '', confidence: extractedData.solution.confidence, multiline: true },
        { key: 'targetCustomer', label: 'Target Customer', value: fieldValues.targetCustomer || '', confidence: extractedData.targetCustomer.confidence, multiline: true },
        { key: 'marketSize', label: 'Market Size ($)', value: fieldValues.marketSize || '', confidence: extractedData.marketSize.confidence },
        { key: 'fundingTarget', label: 'Funding Target ($)', value: fieldValues.fundingTarget || '', confidence: extractedData.fundingTarget.confidence },
        { key: 'teamSize', label: 'Team Size', value: fieldValues.teamSize || '', confidence: extractedData.teamSize.confidence },
        { key: 'location', label: 'Location', value: fieldValues.location || '', confidence: extractedData.location.confidence },
        { key: 'foundedYear', label: 'Founded Year', value: fieldValues.foundedYear || '', confidence: extractedData.foundedYear.confidence },
        { key: 'competitors', label: 'Competitors', value: fieldValues.competitors || '', confidence: extractedData.competitors.confidence },
      ]
    : [];

  const updateFieldValue = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleConfirm = () => {
    if (!extractedData) return;

    // Build profile from reviewed values — the helper leaves any field the
    // founder didn't confirm UNKNOWN (no silent 'pre-seed'/$0/current-year/
    // team-of-1/is_actively_raising:false defaults; those polluted scoring).
    const profile = buildReviewedDeckProfile(fieldValues);

    // Save to store
    setStartupProfile(profile);
    setProfileMethod?.('deck');
    track('pitch_deck_analyzed');

    // Navigate to complete
    router.push('/(onboarding)/complete');
  };

  const formatNumber = (value: string): string => {
    const num = value.replace(/[^0-9]/g, '');
    if (!num) return '';
    return Number(num).toLocaleString();
  };

  if (!extractedData) {
    // Honest error state — a corrupt/missing param must not strand the founder on
    // a permanent "Loading…". Offer a way back to re-run the upload.
    if (parseError) {
      return (
        <View className="flex-1 items-center justify-center px-8" style={{ backgroundColor: colors.surface.secondary }}>
          <AlertTriangle size={40} color={colors.warning} />
          <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mt-4 text-center">
            We couldn&apos;t read your pitch-deck results
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm mt-2 text-center">
            Something went wrong processing the deck. Go back and try uploading it again.
          </Text>
          <AnimatedPressable
            onPress={() => router.back()}
            className="mt-6 px-6 py-3 rounded-xl flex-row items-center gap-2"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <RotateCcw size={16} color="#fff" />
            <Text className="text-white font-semibold">Go Back &amp; Retry</Text>
          </AnimatedPressable>
        </View>
      );
    }
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
        <ActivityIndicator color={colors.primary[600]} />
        <Text style={{ color: colors.text.secondary }} className="mt-3">Loading…</Text>
      </View>
    );
  }

  const lowConfidenceCount = fields.filter((f) => f.confidence < 70).length;

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          {/* Header */}
          <Animated.View
            entering={FadeInDown.delay(100).duration(500)}
            className="px-5 pt-4 pb-4"
          >
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => router.back()}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <View className="flex-row gap-2">
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
              </View>
              <View className="w-10" />
            </View>
          </Animated.View>

          {/* Title & Confidence */}
          <Animated.View
            entering={FadeInDown.delay(200).duration(500)}
            className="px-5 pb-4"
          >
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
              Review Extracted Data
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              Verify and edit the information below
            </Text>

            {/* Confidence Summary */}
            <View
              className="flex-row items-center gap-3 mt-4 p-3 rounded-xl"
              style={{
                backgroundColor:
                  extractedData.overallConfidence >= 80
                    ? colors.traffic.greenLight
                    : extractedData.overallConfidence >= 60
                    ? colors.amber.light
                    : colors.red.light,
              }}
            >
              {extractedData.overallConfidence >= 80 ? (
                <CheckCircle size={20} color={colors.traffic.green} />
              ) : (
                <AlertTriangle size={20} color={extractedData.overallConfidence >= 60 ? colors.amber.main : colors.red.main} />
              )}
              <View className="flex-1">
                <Text
                  style={{
                    color:
                      extractedData.overallConfidence >= 80
                        ? colors.traffic.green
                        : extractedData.overallConfidence >= 60
                        ? colors.amber.main
                        : colors.red.main,
                  }}
                  className="text-sm font-semibold"
                >
                  {extractedData.overallConfidence}% Overall Confidence
                </Text>
                {lowConfidenceCount > 0 && (
                  <Text style={{ color: colors.text.secondary }} className="text-xs">
                    {lowConfidenceCount} field{lowConfidenceCount > 1 ? 's' : ''} may need review
                  </Text>
                )}
              </View>
            </View>
          </Animated.View>

          {/* Fields */}
          <ScrollView
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <Animated.View entering={FadeInUp.delay(300).duration(500)} className="gap-4 pb-8">
              {fields.map((field) => (
                <View key={field.key}>
                  <View className="flex-row items-center justify-between mb-2">
                    <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">
                      {field.label}
                    </Text>
                    <View className="flex-row items-center gap-1">
                      <View
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getConfidenceColor(field.confidence) }}
                      />
                      <Text
                        style={{ color: getConfidenceColor(field.confidence) }}
                        className="text-xs"
                      >
                        {field.confidence}%
                      </Text>
                    </View>
                  </View>

                  {editingField === field.key ? (
                    <View>
                      <TextInput
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 2,
                          borderColor: colors.primary[500],
                          borderRadius: 12,
                          padding: 12,
                          color: colors.text.primary,
                          fontSize: 15,
                          minHeight: field.multiline ? 100 : undefined,
                          textAlignVertical: field.multiline ? 'top' : 'center',
                        }}
                        value={fieldValues[field.key]}
                        onChangeText={(text) => {
                          if (field.key === 'marketSize' || field.key === 'fundingTarget') {
                            updateFieldValue(field.key, formatNumber(text));
                          } else {
                            updateFieldValue(field.key, text);
                          }
                        }}
                        multiline={field.multiline}
                        autoFocus
                        onBlur={() => setEditingField(null)}
                        keyboardType={
                          ['marketSize', 'fundingTarget', 'teamSize', 'foundedYear'].includes(field.key)
                            ? 'number-pad'
                            : 'default'
                        }
                      />
                    </View>
                  ) : (
                    <Pressable
                      onPress={() => setEditingField(field.key)}
                      style={{
                        backgroundColor: colors.surface.primary,
                        borderWidth: 1,
                        borderColor: field.confidence < 70 ? colors.amber.main : colors.border.light,
                        borderRadius: 12,
                        padding: 12,
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Text
                        style={{ color: colors.text.primary, flex: 1 }}
                        className="text-sm leading-relaxed"
                        numberOfLines={field.multiline ? 4 : 1}
                      >
                        {field.key === 'marketSize' || field.key === 'fundingTarget'
                          ? `$${formatNumber(fieldValues[field.key])}`
                          : fieldValues[field.key]}
                      </Text>
                      <Edit3 size={16} color={colors.text.tertiary} style={{ marginLeft: 8 }} />
                    </Pressable>
                  )}
                </View>
              ))}
            </Animated.View>
          </ScrollView>

          {/* Actions */}
          <Animated.View
            entering={FadeInUp.delay(400).duration(500)}
            className="px-5 pb-8 pt-4"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <AnimatedPressable
              onPress={handleConfirm}
              className="rounded-xl py-4 flex-row items-center justify-center gap-2"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <CheckCircle size={20} color={colors.text.inverse} />
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Looks Good, Continue
              </Text>
            </AnimatedPressable>

            <Pressable
              onPress={() => router.back()}
              className="py-3 mt-2 flex-row items-center justify-center gap-2"
            >
              <RotateCcw size={16} color={colors.text.tertiary} />
              <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                Upload different file
              </Text>
            </Pressable>
          </Animated.View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}
